import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import api from '../api';
import Spinner from './Spinner';
import SciencePracticalAccessDialog from './SciencePracticalAccessDialog';
import { hasActivePaidFeature } from '../utils/premiumAccess';
import { isHighSchoolSectionRoute } from '../utils/authUtils';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children,
  requireAdmin = false 
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { openLogin } = useAuthModal();
  const location = useLocation();
  const navigate = useNavigate();

  const getPremiumFeature = (pathname: string) => {
    const normalizedPath = pathname.toLowerCase();

    if (normalizedPath.includes('/check-result')) {
      if (normalizedPath.includes('/jamb')) return { key: 'jambResultCheck', label: 'Check JAMB Results' };
      if (normalizedPath.includes('/waec')) return { key: 'waecResultCheck', label: 'Check WAEC Results' };
    }

    if (normalizedPath.includes('/mock-test/jamb')) return { key: 'jambMockTest', label: 'JAMB Mock Test' };
    if (normalizedPath.includes('/mock-test/waec')) return { key: 'waecMockTest', label: 'WAEC Mock Test' };
    if (normalizedPath === '/labs/ohms-law' || normalizedPath === '/labs/microscopy' || normalizedPath === '/labs/refraction-through-glass-block') return null;
    if (normalizedPath.startsWith('/labs')) return { key: 'labs', label: 'Labs' };
    if (normalizedPath.startsWith('/language-hub')) return { key: 'languageHub', label: 'Language Hub' };
    if (normalizedPath.startsWith('/jamb')) return { key: 'jamb', label: 'Explore JAMB' };
    if (normalizedPath.startsWith('/waec')) return { key: 'waec', label: 'Explore WAEC' };
    return null;
  };

  const isScienceUser = (targetUser: typeof user) => {
    if (!targetUser) return false;
    return String(targetUser.department || '').trim().toLowerCase() === 'science';
  };

  const sciencePracticalRoutes = [
    '/labs/titration',
    '/labs/simple-pendulum',
    '/labs/ohms-law',
    '/labs/refraction-through-glass-block',
    '/labs/measurement-and-units',
  ];

  // Allow either the public auth context or a separate admin session stored in localStorage
  // Admin session keys: admin_auth_token, admin_user
  const [adminVerified, setAdminVerified] = useState<boolean | null>(null);
  const [adminChecking, setAdminChecking] = useState(false);

  useEffect(() => {
    let mounted = true;
    const checkAdmin = async () => {
        const token = typeof window !== 'undefined' ? localStorage.getItem('admin_auth_token') : null;
        console.debug('[ProtectedRoute] admin token present:', !!token);
        if (!token) {
          console.debug('[ProtectedRoute] no admin token found');
          if (mounted) setAdminVerified(false);
          return;
        }
        try {
          setAdminChecking(true);
          console.debug('[ProtectedRoute] verifying admin token via /admin/auth/me');
          const res = await api.get('/admin/auth/me');
          console.debug('[ProtectedRoute] /admin/auth/me response:', res?.status, res?.data);
          if (mounted) setAdminVerified(res.data?.success && res.data?.data?.role === 'admin');
        } catch (e: any) {
          // log detailed error for debugging, then clear invalid admin session
          console.error('[ProtectedRoute] admin check failed:', e?.response?.status, e?.response?.data || e.message);
          try { localStorage.removeItem('admin_auth_token'); localStorage.removeItem('admin_user'); } catch (err) {}
          if (mounted) setAdminVerified(false);
        } finally {
          if (mounted) setAdminChecking(false);
        }
    };
    checkAdmin();
    return () => { mounted = false; };
  }, []);

  // If still loading, show loading state.
  // For admin routes we must wait until adminVerified is known (not null)
  if (isLoading || (requireAdmin && (adminChecking || adminVerified === null))) {
    return <Spinner />;
  }

  // For admin routes
  if (requireAdmin) {
    // Check if user is admin either through public auth or admin session
    const isAdmin = (isAuthenticated && user?.role === 'admin') || adminVerified === true;
    
    if (!isAdmin) {
      // Clear any invalid admin session
      localStorage.removeItem('admin_auth_token');
      localStorage.removeItem('admin_user');
      return <Navigate to="/admin/login" state={{ from: location }} replace />;
    }
    return <>{children}</>;
  }

  const hasFullPremiumAccess = Boolean(
    user?.isSubscribed ||
    (user as any)?.isGoldMember ||
    (user as any)?.isSchoolSubscribed
  );

  const premiumFeature = getPremiumFeature(location.pathname);
  const hasFeatureAccess = Boolean(premiumFeature && hasActivePaidFeature(
    user?.paidFeatures,
    user?.paidFeatureExpiries,
    premiumFeature.key
  ));

  const normalizedPath = location.pathname.toLowerCase().replace(/\/+$/, '') || '/';
  const isScienceRestrictedRoute = sciencePracticalRoutes.some((route) => (
    normalizedPath === route || normalizedPath.startsWith(`${route}/`)
  ));
  const showScienceRestriction = isAuthenticated && isScienceRestrictedRoute && !isScienceUser(user);
  if (showScienceRestriction) {
    return (
      <SciencePracticalAccessDialog
        open
        department={user?.department}
        onClose={() => navigate('/labs', { replace: true })}
      />
    );
  }

  if (premiumFeature && !hasFullPremiumAccess && !hasFeatureAccess) {
    return <Navigate to="/payment" state={{ from: location, featureKey: premiumFeature.key, featureLabel: premiumFeature.label, selectedFeatures: [premiumFeature.key] }} replace />;
  }

  // For non-admin protected routes
  if (!isAuthenticated) {
    if (isHighSchoolSectionRoute(location.pathname)) {
      return <Navigate to="/high-school/login" state={{ from: location }} replace />;
    }
    openLogin();
    return <>{children}</>;
  }

  return <>{children}</>;
};

export default ProtectedRoute;